import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { deleteRequest, getRequest, patchRequest, postRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';

export type TerritoryView = {
  uuid: StringifiedUUID;
  hierarchy_model_uuid: StringifiedUUID;
  name: string;
  description: string | null;
  order: number;
  created_at: string;
  updated_at: string;
};

export type TerritoryViewRequest = {
  name: string;
  description?: string;
};

const apiUrl = (hierarchyModelId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${hierarchyModelId}/territory-views/`;

const getTerritoryViews = (hierarchyModelId: StringifiedUUID) =>
  getRequest<null, TerritoryView[]>(apiUrl(hierarchyModelId));

const useGetTerritoryViewsQuery = (hierarchyModelId: StringifiedUUID, options: { enabled?: boolean } = {}) => {
  const QUERY_KEY = ['hierarchy-models', hierarchyModelId, 'territory-views'];
  const queryClient = useQueryClient();

  const query = useQuery<TerritoryView[], APIError>(QUERY_KEY, () => getTerritoryViews(hierarchyModelId), {
    enabled: options.enabled !== false && !!hierarchyModelId,
  });

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const createTerritoryView =
  (csrfToken: string, hierarchyModelId: StringifiedUUID) => (values: TerritoryViewRequest) => {
    return postRequest<TerritoryViewRequest, TerritoryView>(apiUrl(hierarchyModelId), values, csrfToken);
  };

const useCreateTerritoryViewMutation = (
  csrfToken: string,
  hierarchyModelId: StringifiedUUID,
  options: UseMutationOptions<TerritoryView, APIError, TerritoryViewRequest> = {},
) => {
  return {
    mutation: useMutation<TerritoryView, APIError, TerritoryViewRequest>(
      createTerritoryView(csrfToken, hierarchyModelId),
      options,
    ),
  };
};

const updateTerritoryView =
  (csrfToken: string, hierarchyModelId: StringifiedUUID, territoryViewId: StringifiedUUID) =>
  (values: TerritoryViewRequest) => {
    return patchRequest<TerritoryViewRequest, TerritoryView>(
      `${apiUrl(hierarchyModelId)}${territoryViewId}/`,
      values,
      csrfToken,
    );
  };

const useUpdateTerritoryViewMutation = (
  csrfToken: string,
  hierarchyModelId: StringifiedUUID,
  territoryViewId: StringifiedUUID,
  options: UseMutationOptions<TerritoryView, APIError, TerritoryViewRequest> = {},
) => {
  return {
    mutation: useMutation<TerritoryView, APIError, TerritoryViewRequest>(
      updateTerritoryView(csrfToken, hierarchyModelId, territoryViewId),
      options,
    ),
  };
};

const deleteTerritoryView =
  (csrfToken: string, hierarchyModelId: StringifiedUUID) => (territoryViewId: StringifiedUUID) => {
    return deleteRequest<null, void>(`${apiUrl(hierarchyModelId)}${territoryViewId}/`, null, csrfToken);
  };

const useDeleteTerritoryViewMutation = (
  csrfToken: string,
  hierarchyModelId: StringifiedUUID,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<void, APIError, StringifiedUUID>(deleteTerritoryView(csrfToken, hierarchyModelId), options),
  };
};

export type AccessibleTerritoryViewsResponse = {
  has_main_tree_access: boolean;
  accessible_view_ids: StringifiedUUID[];
};

const getAccessibleTerritoryViews = (hierarchyModelId: StringifiedUUID) =>
  getRequest<null, AccessibleTerritoryViewsResponse>(
    `/api/v1/tq/hierarchy-models/${hierarchyModelId}/accessible-territory-views/`,
  );

const useGetAccessibleTerritoryViewsQuery = (
  hierarchyModelId: StringifiedUUID,
  options: { enabled?: boolean } = {},
) => {
  const QUERY_KEY = ['hierarchy-models', hierarchyModelId, 'accessible-territory-views'];
  const queryClient = useQueryClient();

  const query = useQuery<AccessibleTerritoryViewsResponse, APIError>(
    QUERY_KEY,
    () => getAccessibleTerritoryViews(hierarchyModelId),
    {
      enabled: options.enabled !== false && !!hierarchyModelId,
    },
  );

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

export const TerritoryViewsService = {
  useGetTerritoryViewsQuery,
  useCreateTerritoryViewMutation,
  useUpdateTerritoryViewMutation,
  useDeleteTerritoryViewMutation,
  useGetAccessibleTerritoryViewsQuery,
};
